import React, { useEffect, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import HomeService from "../../services/HomeService";
import { getContentLanguage } from "../../services/LangPrefs";
import { TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import {
    buildReadStoryPath,
    getHomeStoryRowId,
    getLocalizedStoryFromHomeStoryRo,
    truncateText,
} from "../browse-stories/storyRoUtils";

const CATALOG_PUZZLE_TYPES = new Set(["CROSSWORD", "WORD_SEARCH", "HIDDEN_MESSAGE"]);

/**
 * 3–5 latest original-story permalinks for catalog / listing pages.
 */
export default function LatestStoriesLinks({ puzzleKind }) {
    const { t } = usePagesTranslation();
    const contentLanguage = getContentLanguage();
    const [items, setItems] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const puzzleType = CATALOG_PUZZLE_TYPES.has(puzzleKind) ? puzzleKind : undefined;
        HomeService.getStoriesPage({
            page: 0,
            pageSize: 5,
            language: contentLanguage,
            puzzleType,
        }).then((result) => {
            if (cancelled) return;
            const content = Array.isArray(result.content) ? result.content : [];
            setItems(content);
        });
        return () => {
            cancelled = true;
        };
    }, [contentLanguage, puzzleKind]);

    if (!items.length) return null;

    return (
        <Box component="section" aria-labelledby="latest-stories-heading" sx={{ mt: 2.5 }}>
            <Typography
                id="latest-stories-heading"
                variant="subtitle1"
                component="h3"
                sx={{ fontWeight: 700, mb: 1, color: TEXT.primary }}
            >
                {t("stories.latest-heading", "Latest stories")}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {items.map((row) => {
                    const id = getHomeStoryRowId(row);
                    const href = buildReadStoryPath(id);
                    if (!href) return null;
                    const { title, text } = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
                    const heading = (title || row?.templateTitle || "").trim();
                    if (!heading) return null;
                    const excerpt = truncateText(text, 120);
                    return (
                        <Box component="li" key={id} sx={{ mb: 1 }}>
                            <Link
                                component={RouterLink}
                                to={href}
                                sx={{ fontWeight: 600, color: TEXT.primary, textDecoration: "underline" }}
                            >
                                {heading}
                            </Link>
                            {excerpt ? (
                                <Typography variant="body2" sx={{ color: TEXT.secondary, mt: 0.25, lineHeight: 1.5 }}>
                                    {excerpt}
                                </Typography>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
            <Typography variant="body2" sx={{ mt: 1.25 }}>
                <Link component={RouterLink} to="/stories" sx={{ fontWeight: 600, color: "inherit" }}>
                    {t("stories.back-list", "All stories")}
                </Link>
            </Typography>
        </Box>
    );
}

LatestStoriesLinks.propTypes = {
    puzzleKind: PropTypes.string,
};
